import * as React from 'react';
import styled, { css } from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogIsCatalogOnly, getCatalogIsTimedPlus } from '@/redux/modules/catalog/catalogs/catalog.selectors';

import useGetCatalogStatus, { CatalogStatus as CatalogStatusEnum } from '../../hooks/useGetCatalogStatus';
import CatalogOnlyAuctionStatus from './components/CatalogOnlyAuctionStatus';
import TimedOrLiveAuctionStatus from './components/TimedOrLiveAuctionStatus';
import TimedPlusAuctionStatus from './components/TimedPlusAuctionStatus';

export type CatalogStatusProps = {
    catalogId: number;
    forceIsLive: boolean;
    setForceIsLive: (force: boolean) => void;
};

const CatalogStatus = ({ catalogId, forceIsLive, setForceIsLive }: CatalogStatusProps) => {
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const currentCatalogStatus = useGetCatalogStatus(catalogId, forceIsLive);

    return (
        <StyledCatalogStatus
            $status={currentCatalogStatus}
            data-testid="catalogStatus"
            suppressHydrationWarning
        >
            {isCatalogOnly ? (
                <CatalogOnlyAuctionStatus catalogId={catalogId} />
            ) : isTimedPlus ? (
                <TimedPlusAuctionStatus
                    catalogId={catalogId}
                    setForceIsLive={setForceIsLive}
                />
            ) : (
                <TimedOrLiveAuctionStatus
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                    setForceIsLive={setForceIsLive}
                />
            )}
        </StyledCatalogStatus>
    );
};

export default CatalogStatus;

const StyledCatalogStatus = styled.div<{
    $status: CatalogStatusEnum;
}>`
    background-color: ${({ theme }) => theme.colors.blueLight};
    border-top: none;
    padding: 32px 24px;
    min-width: 290px;
    height: 100%;
    ${({ $status }) => {
        if ($status === CatalogStatusEnum.Declined) {
            return css`
                background-color: ${({ theme }) => theme.colors.red400};
                min-height: 360px;
            `;
        } else if ($status === CatalogStatusEnum.Approved) {
            return css`
                background-color: ${({ theme }) => theme.colors.blueLight};
            `;
        } else if ($status === CatalogStatusEnum.Pending) {
            return css`
                background-color: ${({ theme }) => theme.colors.grey500};
                min-height: 360px;
            `;
        } else if ($status === CatalogStatusEnum.Live) {
            return css`
                background-color: ${({ theme }) => theme.colors.beige100};
            `;
        } else if ($status === CatalogStatusEnum.PendingCatalogOnly) {
            return css`
                background-color: ${({ theme }) => theme.colors.grey500};
            `;
        }
        return;
    }}
`;
